import { FunctionComponent, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { useMutation } from '@apollo/client'
import { SET_MAIN_TRUSTEE, UPDATE_TRUSTEE_LINK } from '@/modules/API/daycareGroup/trusteeLinkHandler'
import styles from './editAdminDialog.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import { DaycareTrustee } from '@/types/DaycareGroup'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface EditAdminDialogProps {
    open: boolean
    handleClose: () => void
    groupId: number
    trusteeData: DaycareTrustee | null
    refetchGroups: () => void
}

const EditAdminDialog: FunctionComponent<EditAdminDialogProps> = ({
    open,
    handleClose,
    groupId,
    refetchGroups,
    trusteeData
}: EditAdminDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [isMainAdmin, setIsMainAdmin] = useState<boolean>(trusteeData?.main || false)
    const [canWrite, setCanWrite] = useState<boolean>(trusteeData?.canWrite || false)

    function handleConfirm(): void {
        if (canWrite !== trusteeData?.canWrite) {
            updateTrusteeLink()
        }

        if (isMainAdmin !== trusteeData?.main) {
            setMainTrustee()
        }
    }

    const [setMainTrustee] = useMutation(SET_MAIN_TRUSTEE, {
        variables: {
            groupId: groupId,
            trusteeId: trusteeData?.id
        },
        onCompleted: () => {
            showNotificationSuccess(t('adminUpdated'))

            refetchGroups()
            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorUpdatedAdmin'))
        }
    })

    const [updateTrusteeLink] = useMutation(UPDATE_TRUSTEE_LINK, {
        variables: {
            groupId: groupId,
            trusteeId: trusteeData?.id,
            canWrite: canWrite
        },
        onCompleted: () => {
            showNotificationSuccess(t('adminUpdated'))

            refetchGroups()
            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorUpdatedAdmin'))
        }
    })

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('editAdmin')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.CheckBoxContainer}>
                    <Checkbox
                        className={styles.CheckBox}
                        text={t('isMainAdmin')}
                        checked={isMainAdmin}
                        onChange={setIsMainAdmin}
                    />
                    <Checkbox
                        className={styles.CheckBox}
                        text={t('canWrite')}
                        checked={canWrite}
                        onChange={setCanWrite}
                    />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={'update'}
                        onClick={handleConfirm}
                        disabled={isMainAdmin === trusteeData?.main && canWrite === trusteeData?.canWrite}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default EditAdminDialog
